import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, TrendingUp, Eye } from 'lucide-react';

const RANGE_OPTIONS = [
  { days: 1, label: '1g' },
  { days: 7, label: '7g' },
  { days: 15, label: '15g' },
  { days: 30, label: '30g' },
  { days: 90, label: '90g' },
  { days: 360, label: '1a' },
];

export const AnalyticsPanel = ({ API, lang }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [rangeDays, setRangeDays] = useState(7);

  const load = useCallback(async (days) => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/admin/analytics?days=${days}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Errore nel caricamento');
      const d = await res.json();
      setData(d);
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  }, [API]);

  useEffect(() => {
    load(rangeDays);
  }, [load, rangeDays]);

  const reset = async () => {
    if (!window.confirm(lang === 'it' ? 'Azzerare tutti i dati di analytics?' : 'Reset all analytics data?')) return;
    try {
      await fetch(`${API}/admin/analytics`, {
        method: 'DELETE',
        credentials: 'include',
      });
      await load(rangeDays);
    } catch (e) {
      console.error('Analytics reset failed:', e);
    }
  };

  if (loading && !data) return <p className="text-black/60">{lang === 'it' ? 'Caricamento…' : 'Loading…'}</p>;
  if (err) return <p className="text-red-700 font-bold">{err}</p>;
  if (!data) return null;

  const maxDaily = Math.max(1, ...data.daily_views.map((d) => d.count));
  const maxSection = Math.max(1, ...data.top_sections.map((s) => s.count));
  const maxItem = Math.max(1, ...data.top_items.map((s) => s.count));

  const formatDay = (iso) => {
    const d = new Date(iso + 'T00:00:00Z');
    const dayNames = lang === 'it'
      ? ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${dayNames[d.getUTCDay()]} ${String(d.getUTCDate()).padStart(2, '0')}`;
  };
  const formatShort = (iso) => {
    const d = new Date(iso + 'T00:00:00Z');
    return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const total = data.daily_views.length;
  const isLong = total > 15;
  const barMinWidth = isLong ? 'w-2.5' : 'w-10';
  const labelStep = Math.max(1, Math.ceil(total / 10));
  const rangeLabel = (() => {
    const map = { 1: lang === 'it' ? 'Oggi' : 'Today',
      7: lang === 'it' ? 'Ultimi 7 giorni' : 'Last 7 days',
      15: lang === 'it' ? 'Ultimi 15 giorni' : 'Last 15 days',
      30: lang === 'it' ? 'Ultimi 30 giorni' : 'Last 30 days',
      90: lang === 'it' ? 'Ultimi 90 giorni' : 'Last 90 days',
      360: lang === 'it' ? 'Ultimo anno' : 'Last year' };
    return map[data.range_days] || `${data.range_days}g`;
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap" data-testid="analytics-range-filter">
        <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-black/50 mr-1">
          {lang === 'it' ? 'Periodo:' : 'Range:'}
        </span>
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.days}
            type="button"
            onClick={() => setRangeDays(opt.days)}
            data-testid={`analytics-range-${opt.days}`}
            className={`px-3 h-8 text-xs font-bold border-[1.5px] border-black rounded-full transition ${
              rangeDays === opt.days ? 'bg-black text-white' : 'bg-white text-black hover:bg-black/5'
            }`}
          >{opt.label}</button>
        ))}
        {loading && <span className="text-xs text-black/50 ml-1">…</span>}
      </div>

      <section className="bg-white border-[1.5px] border-black rounded-[16px] p-5 md:p-6 flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-black/50 mb-1 flex items-center gap-1.5">
              <TrendingUp size={13} /> {rangeLabel}
            </p>
            <div className="flex items-baseline gap-3">
              <p className="font-display text-6xl md:text-7xl text-black leading-none" data-testid="analytics-total-count">{data.home_views_total ?? data.home_views_week}</p>
              <p className="text-black/60 font-semibold">{lang === 'it' ? 'aperture del menù' : 'menu opens'}</p>
            </div>
          </div>
        </div>

        <div className="flex items-end gap-[2px] h-28 overflow-x-auto">
          {data.daily_views.map((d, i) => (
            <div key={d.day} className={`flex flex-col items-center justify-end ${barMinWidth} flex-shrink-0`}>
              <div className="relative w-full flex items-end justify-center" style={{ height: '90px' }}>
                <div
                  className="w-full bg-black rounded-t-sm"
                  style={{ height: `${Math.max(2, (d.count / maxDaily) * 90)}px` }}
                  title={`${d.day}: ${d.count}`}
                />
                {!isLong && d.count > 0 && (
                  <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold">{d.count}</span>
                )}
              </div>
              {(i % labelStep === 0 || i === total - 1) && (
                <span className="text-[9px] text-black/60 mt-1 whitespace-nowrap">
                  {isLong ? formatShort(d.day) : formatDay(d.day)}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section className="bg-white border-[1.5px] border-black rounded-[16px] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl text-black flex items-center gap-2"><BarChart3 size={18} /> {lang === 'it' ? 'Sezioni più viste' : 'Most viewed sections'}</h3>
          </div>
          {data.top_sections.length === 0 ? (
            <p className="text-sm text-black/50 italic">{lang === 'it' ? 'Nessun dato ancora.' : 'No data yet.'}</p>
          ) : (
            <ul className="space-y-2">
              {data.top_sections.map((s) => (
                <li key={s.ref}>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <span className="font-bold text-sm text-black truncate">{s.label || s.ref}</span>
                    <span className="font-display text-base text-black whitespace-nowrap">{s.count}</span>
                  </div>
                  <div className="h-1.5 bg-black/10 rounded-full overflow-hidden">
                    <div className="h-full bg-black" style={{ width: `${(s.count / maxSection) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white border-[1.5px] border-black rounded-[16px] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl text-black flex items-center gap-2"><Eye size={18} /> {lang === 'it' ? 'Piatti più cliccati' : 'Most clicked dishes'}</h3>
          </div>
          {data.top_items.length === 0 ? (
            <p className="text-sm text-black/50 italic">{lang === 'it' ? 'Nessun dato ancora.' : 'No data yet.'}</p>
          ) : (
            <ul className="space-y-2">
              {data.top_items.map((s) => (
                <li key={s.ref}>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <span className="font-bold text-sm text-black truncate">{s.label || s.ref}</span>
                    <span className="font-display text-base text-black whitespace-nowrap">{s.count}</span>
                  </div>
                  <div className="h-1.5 bg-black/10 rounded-full overflow-hidden">
                    <div className="h-full bg-black" style={{ width: `${(s.count / maxItem) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => load(rangeDays)} className="pill-btn text-sm" data-testid="analytics-reload-btn">
          {lang === 'it' ? 'Ricarica' : 'Reload'}
        </button>
        <button type="button" onClick={reset} className="pill-btn text-sm text-red-700 border-red-700 hover:bg-red-700" data-testid="analytics-reset-btn">
          {lang === 'it' ? 'Azzera dati' : 'Reset data'}
        </button>
        <span className="text-xs text-black/50 ml-2">
          {lang === 'it' ? '"Piatti più cliccati" = quante volte è stata ingrandita la foto di un piatto.' : '"Most clicked dishes" = how many times a dish photo was enlarged.'}
        </span>
      </div>
    </div>
  );
};

export default AnalyticsPanel;
